import { Suspense } from 'react';
import * as Sentry from '@sentry/react';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AxiosError } from 'axios';
import { Outlet } from 'react-router-dom';
// Hooks
// Store
import { AppProvider } from '@/store/AppContext';
// Sentry init
import { isInSentryExceptionsAllowList } from '@/services/sentryAllowList';
// i18n
import { I18n } from './i18n';

// Sentry init
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.replayIntegration(),
  ],

  beforeSend(event, hint) {
    const error = hint.originalException;
    if (error instanceof AxiosError) {
      if (isInSentryExceptionsAllowList(error)) {
        return null;
      }
      return {
        ...event,
        extra: {
          ...event.extra,
          error: error.response?.data,
        },
      };
    }
    return event;
  },

  enabled: !import.meta.env.DEV,
  environment: import.meta.env.MODE,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  tracesSampleRate: 1.0,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
      throwOnError: true,
    },
    mutations: {
      throwOnError: true,
    },
  },
});

function App() {
  return (
    <I18n>
      <QueryClientProvider client={queryClient}>
        <AppProvider>
          <Suspense>
            <Outlet />
          </Suspense>
        </AppProvider>
        <ReactQueryDevtools initialIsOpen />
      </QueryClientProvider>
    </I18n>
  );
}

export default App;
